/**
 * useOfflineSync Hook
 *
 * Orchestrates offline survey submission queue and automatic sync.
 * Manages retry logic, idempotency, and audit trail requirements.
 *
 * Features:
 * - Queue submissions for offline processing
 * - Auto-sync when network is restored
 * - Exponential backoff retry (1s, 2s, 4s, 8s, 16s)
 * - Manual sync trigger
 * - Pending submission count tracking
 * - Idempotency key generation
 */

import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { OfflineStorageService } from '../services/offlineStorage';
import { submitResponse } from '../services/responseApi';
import type { SubmitResponseDto } from '../services/responseApi';
import type { PendingSubmission } from '../types/offline';
import { useNetworkStatus } from './useNetworkStatus';

// Maximum retry attempts before exhaustion
const MAX_RETRIES = 5;

/**
 * Hook for managing offline survey submission sync
 *
 * Automatically syncs pending submissions when network is available.
 * Provides methods to queue new submissions and manually trigger sync.
 *
 * @returns Sync status and control methods
 *
 * @example
 * ```typescript
 * function SurveyForm() {
 *   const { isOnline, isSyncing, pendingCount, queueSubmission, syncAll } = useOfflineSync();
 *
 *   const handleSubmit = async (data) => {
 *     try {
 *       await submitResponse(data);
 *     } catch (error) {
 *       if (isNetworkError(error)) {
 *         // Queue for offline sync
 *         queueSubmission(data, 'Survey Title');
 *       }
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       {pendingCount > 0 && (
 *         <button onClick={syncAll}>Sync Now ({pendingCount})</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useOfflineSync() {
  const { isOnline } = useNetworkStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);

  /**
   * Update pending count from storage
   */
  const updatePendingCount = useCallback(() => {
    const queue = OfflineStorageService.getQueue();
    setPendingCount(queue.submissions.length);
    if (queue.lastSyncAt) {
      setLastSyncAt(new Date(queue.lastSyncAt));
    }
  }, []);

  /**
   * Queue a submission for offline processing
   *
   * Generates idempotency key and enriches DTO with offline metadata.
   * Immediately saves to localStorage queue.
   *
   * @param dto - Submission data to queue
   * @param surveyTitle - Optional survey title for UI display
   * @returns Queue entry ID
   */
  const queueSubmission = useCallback(
    (dto: SubmitResponseDto, surveyTitle?: string): string => {
      // Generate unique idempotency key for this submission
      const idempotencyKey = uuidv4();

      // Enrich DTO with offline sync metadata
      const enrichedDto: SubmitResponseDto = {
        ...dto,
        idempotency_key: idempotencyKey,
        was_offline: true,
        offline_completed_at: new Date().toISOString(),
      };

      // Add to queue with retry configuration
      const submissionId = OfflineStorageService.addSubmission({
        idempotencyKey,
        dto: enrichedDto,
        status: 'pending',
        retryCount: 0,
        maxRetries: MAX_RETRIES,
        nextRetryAt: new Date().toISOString(), // Try immediately when online
        surveyTitle,
      });

      // Update UI state
      updatePendingCount();

      return submissionId;
    },
    [updatePendingCount]
  );

  /**
   * Sync a single submission to the backend
   *
   * Handles success (remove from queue) and failure (update retry state).
   * Uses exponential backoff for retry scheduling.
   *
   * @param submission - Submission to sync
   * @returns True if sync successful, false otherwise
   */
  const syncSubmission = useCallback(
    async (submission: PendingSubmission): Promise<boolean> => {
      try {
        // Mark as syncing
        OfflineStorageService.updateSubmission(submission.id, {
          status: 'syncing',
        });

        // Attempt API submission
        await submitResponse(submission.dto);

        // Success: Remove from queue
        OfflineStorageService.removeSubmission(submission.id);
        updatePendingCount();
        return true;
      } catch (error) {
        // Failure: Update retry state
        const newRetryCount = submission.retryCount + 1;
        const isExhausted = newRetryCount >= MAX_RETRIES;

        // Calculate next retry time with exponential backoff
        const nextRetryAt = OfflineStorageService.calculateNextRetry(newRetryCount);

        OfflineStorageService.updateSubmission(submission.id, {
          status: isExhausted ? 'exhausted' : 'failed',
          retryCount: newRetryCount,
          nextRetryAt,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        updatePendingCount();
        return false;
      }
    },
    [updatePendingCount]
  );

  /**
   * Sync all retryable submissions
   *
   * Processes submissions sequentially to avoid overwhelming the server.
   * Only syncs submissions where retry time has arrived.
   */
  const syncAll = useCallback(async (): Promise<void> => {
    // Skip if offline or already syncing
    if (!isOnline || isSyncing) {
      return;
    }

    setIsSyncing(true);

    try {
      // Get submissions ready for retry
      const retryable = OfflineStorageService.getRetryableSubmissions();

      // Process sequentially to avoid overwhelming server
      for (const submission of retryable) {
        await syncSubmission(submission);
      }
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, syncSubmission]);

  /**
   * Auto-sync when coming online
   *
   * Waits 1 second after network restore for stability before syncing.
   */
  useEffect(() => {
    if (isOnline && pendingCount > 0 && !isSyncing) {
      // Wait for network stability before syncing
      const timeoutId = setTimeout(() => {
        syncAll();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [isOnline, pendingCount, isSyncing, syncAll]);

  /**
   * Initialize pending count on mount
   */
  useEffect(() => {
    updatePendingCount();
  }, [updatePendingCount]);

  return {
    /**
     * Whether device is currently online
     */
    isOnline,

    /**
     * Whether sync is currently in progress
     */
    isSyncing,

    /**
     * Number of pending submissions in queue
     */
    pendingCount,

    /**
     * Timestamp of last successful sync
     */
    lastSyncAt,

    /**
     * Queue a submission for offline processing
     */
    queueSubmission,

    /**
     * Manually trigger sync for all pending submissions
     */
    syncAll,
  };
}
